import { GameVars } from "./GameVars";
import { PersistentDataManager } from "./PersistentDataManager";

export class AudioManager {

    private static initialized = false;
    private static loopKey: string | null = null;
    private static loops: Record<string, Phaser.Sound.BaseSound> = {};
    private static pendingPlays: Array<{ key: string; loop: boolean; volume: number; detune: number }> = [];
    private static lastEffectPlayed: string | null = null;
    private static timeLastEffectPlayed = 0;
    private static musicKey: string | null = null;
    private static currentMusic: Phaser.Sound.BaseSound | null = null;
    private static currentMusicVolume = 1;

    public static init(): void {

        AudioManager.initialized = true;
        AudioManager.loopKey = null;
        AudioManager.loops = {};
        AudioManager.pendingPlays = [];
        AudioManager.lastEffectPlayed = null;
        AudioManager.musicKey = null;
        AudioManager.currentMusic = null;
        AudioManager.currentMusicVolume = 1;
        AudioManager.applyMuteState();
        AudioManager.ensureAudioUnlocked();
    }

    public static onSceneChanged(): void {

        if (!AudioManager.initialized) {
            return;
        }

        AudioManager.applyMuteState();
        AudioManager.ensureAudioUnlocked();
    }

    public static playSound(key: string, loop = false, volume = 1, detune = 0): void {

        const scene = GameVars.currentScene;
        if (!scene) {
            return;
        }

        if (!loop && GameVars.gameData?.sfxMuted) {
            return;
        }

        const now = Date.now();
        if (!loop && AudioManager.lastEffectPlayed === key && now - AudioManager.timeLastEffectPlayed < 100) {
            return;
        }

        if (AudioManager.isSoundLocked()) {
            AudioManager.pendingPlays.push({ key, loop, volume, detune });
            AudioManager.ensureAudioUnlocked();
            return;
        }

        AudioManager.lastEffectPlayed = key;
        AudioManager.timeLastEffectPlayed = now;

        if (!loop) {
            scene.sound.playAudioSprite("audiosprite", key, { volume, detune });
            return;
        }

        if (AudioManager.loopKey === key) {
            return;
        }

        if (AudioManager.loopKey) {
            AudioManager.stopSound(AudioManager.loopKey);
        }

        const sound = scene.sound.addAudioSprite("audiosprite");
        AudioManager.loopKey = key;
        AudioManager.loops[key] = sound;
        sound.play(key, { loop: true, volume: GameVars.gameData?.musicMuted ? 0 : volume });
    }

    public static stopSound(key: string): void {

        const sound = AudioManager.loops[key];
        if (sound) {
            sound.stop();
            sound.destroy();
            delete AudioManager.loops[key];
        }

        if (AudioManager.loopKey === key) {
            AudioManager.loopKey = null;
        }
    }

    public static setLoopVolume(volume: number): void {

        const sound = AudioManager.loopKey ? AudioManager.loops[AudioManager.loopKey] : undefined;
        (sound as any)?.setVolume?.(volume);
    }

    public static fadeLoopVolume(volume: number, duration = 500): void {

        const sound = AudioManager.loopKey ? AudioManager.loops[AudioManager.loopKey] : undefined;
        if (sound && GameVars.currentScene) {
            GameVars.currentScene.tweens.add({
                targets: sound,
                volume,
                duration,
                ease: Phaser.Math.Easing.Linear
            });
        }
    }

    public static toggleSfxState(): void {

        PersistentDataManager.updateGameData("sfxMuted", !GameVars.gameData.sfxMuted);

        if (!GameVars.gameData.sfxMuted) {
            AudioManager.playSound("click");
        }
    }

    public static toggleMusicState(): void {

        PersistentDataManager.updateGameData("musicMuted", !GameVars.gameData.musicMuted);
        AudioManager.applyMuteState();
    }

    public static mute(): void {

        PersistentDataManager.updateGameData("musicMuted", true);
        PersistentDataManager.updateGameData("sfxMuted", true);
        AudioManager.applyMuteState();
    }

    public static unmute(): void {

        PersistentDataManager.updateGameData("musicMuted", false);
        PersistentDataManager.updateGameData("sfxMuted", false);
        AudioManager.applyMuteState();
    }

    public static toggleAudioState(): void {

        if (GameVars.gameData.musicMuted && GameVars.gameData.sfxMuted) {
            AudioManager.unmute();
        } else {
            AudioManager.mute();
        }
    }

    public static playMusic(key: string, volume: number, fadeIn = true, fadeInDuration = 2000): void {

        AudioManager.stopMusic();

        const scene = GameVars.currentScene;
        if (!scene) {
            return;
        }

        AudioManager.musicKey = key;
        AudioManager.currentMusicVolume = volume;
        const music = scene.sound.add(key, {
            loop: true,
            volume: fadeIn && !GameVars.gameData?.musicMuted ? 0 : volume
        });
        AudioManager.currentMusic = music;
        music.play();

        if (fadeIn && !GameVars.gameData?.musicMuted) {
            scene.tweens.add({
                targets: music,
                volume,
                duration: fadeInDuration,
                ease: Phaser.Math.Easing.Linear
            });
        }
    }

    public static fadeMusicVolume(volume: number, duration = 500): void {

        if (!GameVars.currentScene || !AudioManager.currentMusic || GameVars.gameData?.musicMuted) {
            return;
        }

        AudioManager.currentMusicVolume = volume;
        GameVars.currentScene.tweens.add({
            targets: AudioManager.currentMusic,
            volume,
            duration,
            ease: Phaser.Math.Easing.Linear
        });
    }

    public static setMusicVolume(volume: number): void {

        AudioManager.currentMusicVolume = volume;
        if (!GameVars.gameData?.musicMuted) {
            (AudioManager.currentMusic as any)?.setVolume?.(volume);
        }
    }

    public static stopMusic(key?: string): void {

        if (AudioManager.currentMusic) {
            AudioManager.currentMusic.stop();
            AudioManager.currentMusic.destroy();
            AudioManager.currentMusic = null;
        }

        if (key) {
            GameVars.currentScene?.sound.stopByKey(key);
        }

        AudioManager.musicKey = null;
    }

    public static onBlur(): void {
        if (GameVars.currentScene?.sound) {
            GameVars.currentScene.sound.mute = true;
        }
    }

    public static onFocus(): void {
        AudioManager.applyMuteState();
        AudioManager.ensureAudioUnlocked();
    }

    private static applyMuteState(): void {

        const soundManager = GameVars.currentScene?.sound;
        if (!soundManager) {
            return;
        }

        soundManager.mute = false;
        const loop = AudioManager.loopKey ? AudioManager.loops[AudioManager.loopKey] : undefined;
        (loop as any)?.setMute?.(!!GameVars.gameData?.musicMuted);
        const music = AudioManager.currentMusic as any;
        if (music) {
            music.setMute?.(!!GameVars.gameData?.musicMuted);
            if (!GameVars.gameData?.musicMuted) {
                music.setVolume?.(AudioManager.currentMusicVolume);
            }
        }
    }

    private static isSoundLocked(): boolean {

        const soundManager: any = GameVars.currentScene?.sound;
        return !!soundManager && (soundManager.locked || soundManager.context?.state === "suspended");
    }

    private static ensureAudioUnlocked(): void {

        const scene = GameVars.currentScene;
        if (!scene?.input || !AudioManager.isSoundLocked()) {
            AudioManager.flushPendingPlays();
            return;
        }

        const unlock = (): void => {
            const soundManager: any = scene.sound;
            soundManager.unlock?.();
            soundManager.context?.resume?.().finally(() => AudioManager.flushPendingPlays());
            scene.input.off("pointerdown", unlock);
        };

        scene.input.once("pointerdown", unlock);
    }

    private static flushPendingPlays(): void {

        const pending = AudioManager.pendingPlays.splice(0);
        pending.forEach(({ key, loop, volume, detune }) => AudioManager.playSound(key, loop, volume, detune));
    }
}
